/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
// Components
import {
    Box,
    Heading,
    Flex,
    Text,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import AutoCorrect from './auto-correct'
const PageHeader = ({
    category,
    searchQuery,
    productSearchResult,
    brResponse,
    enableBloomreachSearch = false,
    brAutoCorrectQuery = '',
    isLoading,
    brDidYouMean = [],
    totalItems
}) => {
    const intl = useIntl()
    const showItemCount = !searchQuery && typeof totalItems === 'number'

    return (
        <Box
            data-testid="sf-product-list-breadcrumb"
            margin={
                searchQuery
                    ? {base: '15px 15px 0', lg: '15px 60px 0'}
                    : {base: '0 15px', lg: '0 60px'}
            }
            minHeight={searchQuery ? {base: '35px', lg: '40px'} : '35px'}
        >
            {searchQuery && (
                <Text as="h1" textStyle="h1" color="black.100">
                    {intl.formatMessage(
                        {
                            id: 'search.results_for',
                            defaultMessage: '{count} Results for “{phrase}”'
                        },
                        {
                            count: brResponse?.numFound || productSearchResult?.total,
                            phrase: brAutoCorrectQuery || searchQuery
                        }
                    )}
                </Text>
            )}
            {enableBloomreachSearch && (
                <AutoCorrect
                    brAutoCorrectQuery={brAutoCorrectQuery}
                    searchQuery={searchQuery}
                    isLoading={isLoading}
                    brDidYouMean={brDidYouMean}
                    enableBloomreachSearch={enableBloomreachSearch}
                />
            )}

            {/* Category Title */}
            <Flex
                alignItems="flex-end"
                width="100%"
                gap="10px"
                justifyContent={{base: 'space-between', lg: 'flex-start'}}
            >
                {!category && !searchQuery && (
                    <Box marginRight={2}>
                        <Skeleton height="29px" width="150px" />
                    </Box>
                )}
                {!category?.c_topCategoryHeading && category?.c_name && (
                    <Heading
                        color="black.100"
                        as="h1"
                        fontSize={{base: '27px', lg: '29px'}}
                        lineHeight="normal"
                        fontWeight="400"
                        minWidth="0"
                        marginY="0"
                    >
                        {`${category?.c_name || searchQuery || ''}`}
                    </Heading>
                )}
                {category?.c_topCategoryHeading && (
                    <Box
                        fontSize={{base: '27px', lg: '29px'}}
                        color="black.100"
                        fontWeight="400"
                        lineHeight="normal"
                        minWidth="0"
                        marginY="0"
                        fontFamily="heading"
                        sx={{
                            '& h1, & h2, & p': {
                                margin: 0
                            },
                            '& p': {
                                color: 'black.100',
                                lineHeight: 'normal',
                                fontWeight: '400',
                                fontFamily: 'heading',
                                fontSize: {base: '27px', lg: '29px'}
                            },
                            '& h1, & h2, & span': {
                                color: 'black.100',
                                lineHeight: 'normal',
                                fontWeight: '400',
                                fontFamily: 'heading',
                                fontSize: {base: '27px', lg: '29px'}
                            }
                        }}
                        dangerouslySetInnerHTML={{__html: category?.c_topCategoryHeading}}
                    />
                )}

                {showItemCount && (
                    <Text
                        color="gray.800"
                        fontSize="14px"
                        fontWeight="300"
                        lineHeight="20px"
                        flexShrink="0"
                        paddingBottom="3px"
                    >
                        {intl.formatMessage(
                            {
                                id: 'selected.filters.count',
                                defaultMessage: '{count} item(s)'
                            },
                            {count: totalItems}
                        )}
                    </Text>
                )}
            </Flex>
        </Box>
    )
}

PageHeader.propTypes = {
    category: PropTypes.object,
    searchQuery: PropTypes.string,
    brResponse: PropTypes.any,
    brAutoCorrectQuery: PropTypes.any,
    brDidYouMean: PropTypes.any,
    enableBloomreachSearch: PropTypes.bool,
    isLoading: PropTypes.bool,
    productSearchResult: PropTypes.object,
    totalItems: PropTypes.number
}

export default PageHeader
